import React, {useEffect, useState} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {DateTime} from 'luxon';
import {Tooltip, ClickAwayListener} from '@mui/material';
import {debounce} from 'debounce';

import {setTriggerRefreshData} from '../features/Statsview/dashboardSlice';

function DownStatusIcon(props) {
  const dispatch = useDispatch();
  const location = useLocation();

  const lastCropviewTimeStamp = useSelector((state) => {
    return state.cropview.lastTripTaskEndTime;
  });
  const processingInProgress = useSelector((state) => {
    return state.cropview.processingInProgress;
  });
  const refreshRequired = useSelector((state) => {
    return state.cropview.refreshRequired;
  });
  const stalestGenTime = useSelector((state) => {
    return state.dashboard.stalestGenTime;
  });
  const triggerRefreshData = useSelector((state) => {
    return state.dashboard.triggerRefreshData;
  });
  const statsviewRefreshInProgress = useSelector((state) => {
    return state.dashboard.refreshInProgress;
  });
  const statsviewRangeSelected = useSelector((state) => {
    return state.dashboard.statsviewRangeSelected;
  });
  const customerSettings = useSelector((state) => {
    return state.app.customerSettings;
  });
  const smallScreen = useSelector((state) => {
    return state.framework.smallScreen;
  });

  const defaultStatusToolTip = `Some services are not available, 
    you may experience some interruptions using the platform`;

  const [serviceStatusActive, setServiceStatusActive] = useState(false);
  const [serviceStatusTooltip, setServiceStatusTooltip] = useState(defaultStatusToolTip);
  const [downStatusUpdateToggle, setdownStatusUpdateToggle] = useState(false);
  const [downStatusState, setDownStatusState] = useState(false);
  const [lastDataTimeStampString, setLastDataTimeStampString] = useState(false);

  useEffect(async () => {
    const downStatusProcessedTemp = await getDownStatus();
    setDownStatusState(downStatusProcessedTemp);
    if (!props.login || props.login !== true) {
      // No need to query for last data timestamp in login page cause it's not used
      const lastDataTimeStampStringTemp = await getLastDataTimeStampString();
      setLastDataTimeStampString(lastDataTimeStampStringTemp);
    }
  }, [downStatusUpdateToggle, refreshRequired, lastCropviewTimeStamp, processingInProgress]);

  useEffect(async () => {
    let newToolTip;

    if (location.pathname == '/cropview') {
      if (processingInProgress) {
        newToolTip = (
          <div>
            {'Data reprocessing in progress for your selected time range.\nDisplayed data may be subject to change.'}
          </div>
        );
        setServiceStatusActive(true);
      } else {
        let refreshText;
        if (refreshRequired) {
          refreshText = (
            <>
              <div>Cropview has not been refreshed in 1 hour</div>
              <div>Please refresh for latest updates</div>
            </>
          );
          setServiceStatusActive(true);
        } else {
          setServiceStatusActive(false);
        }

        let displayTimeString = 'Unavailable';
        if (lastCropviewTimeStamp) {
          displayTimeString = DateTime.fromISO(lastCropviewTimeStamp)
            .setZone(customerSettings.general.timeZone)
            .toFormat('MM/dd/yyyy, hh:mm:ss a ZZZZ');
        }
        const lastCropviewTimeText = (
          <>
            <div>Last data timestamp:</div>
            <div>{displayTimeString}</div>
          </>
        );

        newToolTip = (
          <div>
            {refreshText}
            {lastCropviewTimeText}
          </div>
        );
      }
    } else if (location.pathname == '/statsview' && statsviewRangeSelected) {
      let downText;
      if (downStatusState) {
        downText = <div>{defaultStatusToolTip}</div>;
      }
      setServiceStatusActive(downStatusState || statsviewRefreshInProgress || triggerRefreshData);

      let refreshInProgressText;
      if (statsviewRefreshInProgress || triggerRefreshData) {
        refreshInProgressText = (
          <div>
            {'Data refresh in progress for your selected time range.\nDisplayed data may be subject to change.'}
          </div>
        );
      }

      let lastDataText;
      if (!props.login || props.login !== true) {
        // Append Last data timestamp to Tooltip message
        lastDataText = (
          <>
            <div>Last data timestamp:</div>
            <div>{lastDataTimeStampString || 'Unavailable'}</div>
          </>
        );
      }

      let stalestTimeString;
      if (typeof stalestGenTime !== 'string') {
        stalestTimeString = 'Unavailable';
      } else {
        stalestTimeString = DateTime.fromISO(stalestGenTime)
          .setZone(customerSettings.general.timeZone)
          .toFormat('MM/dd/yyyy, hh:mm:ss a ZZZZ');
      }

      const statsviewText = (
        <>
          <div className='mt-1'>StatsView refreshed at:</div>
          <div className='mt-n1'>
            {stalestTimeString}
            <button style={innerButtonStyle} onClick={statsviewRefreshClick} disabled={triggerRefreshData}>
              <FontAwesomeIcon
                icon={triggerRefreshData || statsviewRefreshInProgress ? 'fas fa-spinner' : 'fas fa-refresh'}
                style={{color: '#ffffff'}}
              />
            </button>
          </div>
        </>
      );

      newToolTip = (
        <div onMouseLeave={debounce(handleMouseExitStatsviewText, 200)}>
          {downText}
          {refreshInProgressText}
          {lastDataText}
          {statsviewText}
        </div>
      );
    } else {
      let downText;
      if (downStatusState) {
        downText = <div>{defaultStatusToolTip}</div>;
        setServiceStatusActive(true);
      } else {
        // reset the tooltip display and string
        newToolTip = ``;
        setServiceStatusActive(false);
      }

      let lastDataText;
      if (!props.login || props.login !== true) {
        // Append Last data timestamp to Tooltip message
        lastDataText = (
          <>
            <div>Last data timestamp:</div>
            <div>{lastDataTimeStampString || 'Unavailable'}</div>
          </>
        );
      }

      newToolTip = (
        <div>
          {downText}
          {lastDataText}
        </div>
      );
    }

    // Update Tooltip Message in state
    setServiceStatusTooltip(newToolTip);
  }, [
    downStatusState,
    lastDataTimeStampString,
    refreshRequired,
    lastCropviewTimeStamp,
    processingInProgress,
    stalestGenTime,
    triggerRefreshData,
    statsviewRefreshInProgress,
  ]);

  async function getLastDataTimeStampString() {
    const serviceStatus = await getServiceStatus();
    // Default Latest Data timestamp string for display
    let displayTimeString = 'None';

    if (serviceStatus.geotabStatus.lastDataTime != null && serviceStatus.faunaStatus.lastDataTime != null) {
      // If Both services last data time is available
      // Parse Last data times
      const geotabLastDataLocalTime = new Date(serviceStatus.geotabStatus.lastDataTime);
      const faunaLastDataLocalTime = new Date(serviceStatus.faunaStatus.lastDataTime);

      // Compare time and use latest time for display
      if (geotabLastDataLocalTime > faunaLastDataLocalTime) {
        displayTimeString = DateTime.fromISO(serviceStatus.geotabStatus.lastDataTime)
          .setZone(customerSettings.general.timeZone)
          .toFormat('MM/dd/yyyy, hh:mm:ss a ZZZZ');
      } else {
        displayTimeString = DateTime.fromISO(serviceStatus.faunaStatus.lastDataTime)
          .setZone(customerSettings.general.timeZone)
          .toFormat('MM/dd/yyyy, hh:mm:ss a ZZZZ');
      }
    } else if (serviceStatus.geotabStatus.lastDataTime != null) {
      // If only geotab last data time is available
      displayTimeString = DateTime.fromISO(serviceStatus.geotabStatus.lastDataTime)
        .setZone(customerSettings.general.timeZone)
        .toFormat('MM/dd/yyyy, hh:mm:ss a ZZZZ');
    } else if (serviceStatus.faunaStatus.lastDataTime != null) {
      // If only fauna last data time is available
      displayTimeString = DateTime.fromISO(serviceStatus.faunaStatus.lastDataTime)
        .setZone(customerSettings.general.timeZone)
        .toFormat('MM/dd/yyyy, hh:mm:ss a ZZZZ');
    }

    return displayTimeString;
  }

  async function getServiceStatus() {
    const serviceStatusResponse = await fetch('/getServiceStatus');
    const serviceStatus = await serviceStatusResponse.json();
    return serviceStatus;
  }

  async function getDownStatus() {
    const downStatusResponse = await fetch('/getDownStatus');
    const downStatusArray = await downStatusResponse.json();
    // default down Status, if return empty array means fauna is down
    let downStatusActive = false;

    // Loop through downStatus in order of response (customer specific -> all)
    for (let i = 0; i < downStatusArray.length; i++) {
      if (downStatusArray[i].downstatus) {
        downStatusActive = true;
      }
    }
    return downStatusActive;
  }

  function toggleDownStatusUpdateToggle() {
    if (smallScreen) {
      setdownStatusUpdateToggle(!downStatusUpdateToggle);
    } else if (location.pathname == '/statsview' && statsviewRangeSelected) {
      setdownStatusUpdateToggle(!downStatusUpdateToggle);
    }
  }
  function handleMouseOver() {
    setdownStatusUpdateToggle(true);
  }
  function handleMouseExit() {
    if (!(location.pathname == '/statsview' && statsviewRangeSelected)) {
      setdownStatusUpdateToggle(false);
    }
  }
  function handleMouseExitStatsviewText() {
    if (downStatusUpdateToggle) {
      setdownStatusUpdateToggle(false);
    }
  }
  async function statsviewRefreshClick() {
    dispatch(setTriggerRefreshData(true));
  }

  const innerButtonStyle = {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
  };

  return (
    <div>
      <Tooltip title={serviceStatusTooltip} open={downStatusUpdateToggle} arrow>
        <button
          style={{
            position: 'relative',
            display: props.login && props.login === true && !serviceStatusActive ? 'none' : 'inline-block',
            border: 'none',
            backgroundColor: props.login && props.login === true ? '#fff' : '#f8f9fc',
          }}
          onClick={toggleDownStatusUpdateToggle}
          onMouseEnter={debounce(handleMouseOver, 200)}
          onMouseLeave={debounce(handleMouseExit, 200)}
        >
          {serviceStatusActive ? (
            <FontAwesomeIcon
              icon='fas fa-triangle-exclamation'
              size={props.small === true ? '1x' : 'xl'}
              style={{color: '#f4c526'}}
            />
          ) : (
            <FontAwesomeIcon
              icon='fas fa-info-circle '
              size={props.small === true ? '1x' : 'xl'}
              style={{color: '#858796'}}
            />
          )}
        </button>
      </Tooltip>
    </div>
  );
}

export {DownStatusIcon};
